import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router';
import { Analytics } from '@vercel/analytics/react';
import { AppProviders } from '@/providers';
import { ScrollBehavior } from '@/components/scroll-behavior';
import { SiteLayout } from '@/routes/site-layout';
import { Home } from '@/routes/home';
import { ServicesIndex } from '@/routes/services-index';
import { ServiceDetail } from '@/routes/service-detail';
import { NotFound } from '@/routes/not-found';

/* Split out of the main bundle. The game, its simulation, its canvas painter
   and its synthesised audio are about a third of the JavaScript on the site,
   and nobody reading the pricing table needs any of it. Next used to code-split
   per route automatically; this is that, done explicitly.

   renderToString has no way to await a lazy chunk, so the prerendered
   /play/stack ships the fallback below rather than the game itself. That costs
   nothing real: the game is a <canvas> the client has to paint anyway, and the
   page's head tags -- which are what a crawler or a share card reads -- are
   injected by the prerender script independently of the markup. */
const PlayStack = lazy(async () => ({
  default: (await import('@/routes/play-stack')).PlayStack,
}));

/**
 * The route table.
 *
 * /play/stack sits outside SiteLayout on purpose. It was a second parallel root
 * layout under Next -- full screen, no chrome, its own locked viewport -- and
 * the links between it and the site are plain anchors, so crossing between the
 * two shells stays a hard navigation exactly as it was.
 *
 * Anything unmatched renders NotFound, which is also what the services routes
 * render while SERVICES_ENABLED is false.
 */
export function App() {
  return (
    <AppProviders>
      <ScrollBehavior />
      <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<ServicesIndex />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route
          path="/play/stack"
          element={
            /* The heading lives in the fallback as well as in the game, so the
               page is never served -- prerendered or mid-chunk-load -- without
               one. The body is already ink, so there is nothing to paint. */
            <Suspense fallback={<h1 className="sr-only">Butter Stack</h1>}>
              <PlayStack />
            </Suspense>
          }
        />
      </Routes>
      <Analytics />
    </AppProviders>
  );
}
